// Roadmap.jsx
import React, { useState } from 'react';
import './Roadmap.css'; // We'll update this CSS too
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const MagicChatRoadmap = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Feedback Form States
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    title: '',
    message: '',
    type: 'feature',
    user_email: '',
    user_name: '',
    tags: []
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [customTag, setCustomTag] = useState('');

  const [activeTab, setActiveTab] = useState('q1');

  const roadmapData = {
    q1: {
      title: "Q1 – Foundation & Core Platform",
      subtitle: "Strengthening the core experience, customization, and developer readiness.",
      status: "in-progress",
      items: [
        {
          title: "✅ COMPLETED",
          features: [
            "Human Handoff / Live Agent Mode",
            "Team Collaboration",
            "Role-Based Access Control",
            "Unified Multi-Website / App Support",
            "ManageExtensions & Community Plugins",
            "Embeddable UI Customization"
          ]
        },
        {
          title: "🚧 IN PROGRESS",
          features: [
            "Public APIs, SDKs & Webhooks",
            "Basic Analytics",
            "Multi-LLM Support",
            "Extended Frontend Framework Support"
          ]
        },
        {
          title: "⏳ PENDING",
          features: [
            "Conversation Memory & User Profiles"
          ]
        }
      ]
    },
    q2: {
      title: "Q2 – Automation, Insights & Enterprise Readiness",
      subtitle: "Turning chat into workflows, automation, and measurable outcomes.",
      status: "planned",
      items: [
        {
          title: "🔁 AI Workflows & Agent Actions",
          features: [
            "Multi-step workflows",
            "Conditional logic",
            "Database & system actions via APIs and webhooks"
          ]
        },
        {
          title: "📬 Omnichannel Inbox",
          features: [
            "Web",
            "WhatsApp",
            "Slack",
            "Email",
            "Unified user identity across channels"
          ]
        },
        {
          title: "🧩 Tool Marketplace & Integrations",
          features: [
            "CRM systems",
            "Zendesk",
            "Stripe",
            "Notion",
            "Databases",
            "Extensible plugin ecosystem"
          ]
        },
        {
          title: "📊 Advanced Analytics & Insights",
          features: [
            "Conversation success rates",
            "Drop-off analysis",
            "Unanswered / failed queries",
            "Intent & performance insights"
          ]
        },
        {
          title: "🔐 Compliance & Security Enhancements",
          features: [
            "Enhanced PII redaction",
            "SOC 2 readiness",
            "GDPR compliance"
          ]
        },
        {
          title: "🧱 Platform & Infrastructure",
          features: [
            "Rate limiting",
            "Token usage caps",
            "Cost alerts & safeguards"
          ]
        }
      ]
    },
    q3: {
      title: "Q3 – Reliability, Global Reach & Rich Media",
      subtitle: "Scaling reliability, adding media capabilities, and going global.",
      status: "planned",
      items: [
        {
          title: "🎧 Audio & Video Chat",
          features: [
            "Real-time audio and video interactions"
          ]
        },
        {
          title: "🌍 Internationalization (i18n)",
          features: [
            "Multi-language support",
            "Localized UI and responses"
          ]
        },
        {
          title: "🛡️ SLA & Reliability Tools",
          features: [
            "Uptime dashboards",
            "Model & system failover mechanisms"
          ]
        }
      ]
    },
    q4: {
      title: "Q4 – Voice & Conversational Automation",
      subtitle: "Advanced voice capabilities and conversational automation.",
      status: "planned",
      items: [
        {
          title: "🎙️ Advanced Voice AI",
          features: [
            "Voice-to-action workflows (Book, Cancel, Update)",
            "Call transcripts",
            "AI-generated call summaries"
          ]
        }
      ]
    },
    q5: {
      title: "Q5 – AI-Native & Autonomous Future",
      subtitle: "Autonomous agents and next-gen telephony integration.",
      status: "future",
      items: [
        {
          title: "🧠 Autonomous & Self-Improving Agents",
          features: [
            "Scheduled agents",
            "Proactive system monitoring",
            "Auto-detection of failed responses",
            "Knowledge gap identification",
            "Human-in-the-loop learning approvals"
          ]
        },
        {
          title: "☎️ SIP Integrations & IVR Replacement",
          features: [
            "SIP-based telephony integrations",
            "AI-powered IVR replacement",
            "End-to-end voice automation"
          ]
        }
      ]
    }
  };

  const feedbackTypes = [
    { value: 'feature', label: 'Feature Request', emoji: '✨' },
    { value: 'bug', label: 'Bug Report', emoji: '🐛' },
    { value: 'improvement', label: 'Improvement', emoji: '🔧' },
    { value: 'general', label: 'General Feedback', emoji: '💬' }
  ];

  const predefinedTags = [
    'ui', 'api', 'mobile', 'performance', 'security',
    'analytics', 'integration', 'customization', 'workflow'
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      setSubscriptionStatus({ type: 'error', message: 'Please enter your email address' });
      return;
    }

    setIsSubscribing(true);
    setSubscriptionStatus(null);

    try {
      const response = await fetch(`https://feedback2.${backend_api_base_url}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name: name || undefined,
          subscription_type: 'feature_updates',
          user_type: 'customer'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscriptionStatus({
          type: 'success',
          message: '🎉 Successfully subscribed! You\'ll receive updates about new features.'
        });
        setEmail('');
        setName('');
      } else {
        setSubscriptionStatus({
          type: 'error',
          message: data.error || 'Subscription failed. Please try again.'
        });
      }
    } catch (error) {
      setSubscriptionStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleFeedbackChange = (e) => {
    const { name, value } = e.target;
    setFeedbackData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagToggle = (tag) => {
    setFeedbackData(prev => {
      const newTags = prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag];
      return { ...prev, tags: newTags };
    });
  };

  const handleAddCustomTag = () => {
    if (customTag.trim() && !feedbackData.tags.includes(customTag.trim())) {
      setFeedbackData(prev => ({
        ...prev,
        tags: [...prev.tags, customTag.trim()]
      }));
      setCustomTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFeedbackData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();

    // Use subscription email/name if feedback email/name is empty
    const userEmail = feedbackData.user_email || email;
    const userName = feedbackData.user_name || name;

    if (!feedbackData.title.trim() || !feedbackData.message.trim() || !userEmail) {
      setFeedbackStatus({
        type: 'error',
        message: 'Please fill in all required fields: title, message, and email.'
      });
      return;
    }

    setIsSubmittingFeedback(true);
    setFeedbackStatus(null);

    try {
      const token = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJjb20uemFsYW5kby5jb25uZXhpb24iLCJpYXQiOjE3Njg4NTc0NTUsImV4cCI6MTc3NDg1NzQ1NSwidXNlcl9pZCI6IlRFTi1MVlVEMCIsInVzZXJfdHlwZSI6Im93bmVyIiwiaXNfc3VwcG9ydCI6ZmFsc2UsImVtYWlsIjoicGlubmFwbGVAc2hhcmtsYXNlcnMuY29tIiwidGVuYW50X2lkIjoicGlubmFwbGUiLCJ0ZW5hbnRfYWNjb3VudF9uYW1lIjoicGlubmFwbGUiLCJyb2xlX3BvbGljeSI6Ilt7XCJyb2xlXCI6IDE5NjYwOC4wfV0ifQ.mrGoPHftEF2pPbW5KkeDr7UuJFjSgUFLfoEsLZ_Oydg";

      const response = await fetch(`https://feedback2.${backend_api_base_url}/submit_feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          type: feedbackData.type,
          title: feedbackData.title,
          message: feedbackData.message,
          user_email: userEmail,
          user_name: userName,
          tags: feedbackData.tags,
          metadata: {
            submitted_from: 'roadmap_page',
            roadmap_quarter: activeTab,
            user_type: 'customer'
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFeedbackStatus({
          type: 'success',
          message: '🎉 Thank you for your feedback! We\'ll review it soon.'
        });

        // Reset form
        setFeedbackData({
          title: '',
          message: '',
          type: 'feature',
          user_email: '',
          user_name: '',
          tags: []
        });


        // Hide form after successful submission (optional)
        setTimeout(() => {
          setShowFeedbackForm(false);
          setFeedbackStatus(null);
        }, 3000);
      } else {
        setFeedbackStatus({
          type: 'error',
          message: data.error || 'Failed to submit feedback. Please try again.'
        });
      }
    } catch (error) {
      setFeedbackStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in-progress': return '#f59e0b';
      case 'planned': return '#3b82f6';
      case 'future': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'planned': return 'Planned';
      case 'future': return 'Future Vision';
      default: return status;
    }
  };

  return (
    <div className="sageion-roadmap">
      <header className="roadmap-header">
        <h1>🚀 Sageion.com Product Roadmap</h1>
        <p className="subtitle">
          This roadmap outlines our phased approach to building a scalable, enterprise-ready AI Agent & Chat Platform.
        </p>
      </header>

      {/* Subscription Section */}
      <div className="subscription-section">
        <div className="subscription-card">
          <div className="subscription-icon">🔔</div>
          <div className="subscription-content">
            <h3>Stay Updated on New Features</h3>
            <p>Get notified when we release new features and updates. No spam, just important announcements.</p>

            <form onSubmit={handleSubscribe} className="subscription-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input"
                />
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="subscribe-button"
                >
                  {isSubscribing ? 'Subscribing...' : 'Subscribe to Updates'}
                </button>
              </div>

              {subscriptionStatus && (
                <div className={`status-message ${subscriptionStatus.type}`}>
                  {subscriptionStatus.message}
                </div>
              )}

              <p className="privacy-note">
                We respect your privacy. Unsubscribe at any time from any email.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Timeline Navigation */}
      <div className="timeline-nav">
        {Object.keys(roadmapData).map((quarter) => (
          <button
            key={quarter}
            onClick={() => setActiveTab(quarter)}
            className={`timeline-tab ${activeTab === quarter ? 'active' : ''}`}
            style={{
              borderLeftColor: getStatusColor(roadmapData[quarter].status),
              borderLeftWidth: '4px'
            }}
          >
            <span className="tab-quarter">{quarter.toUpperCase()}</span>
            <span className="tab-status" style={{ color: getStatusColor(roadmapData[quarter].status) }}>
              {getStatusText(roadmapData[quarter].status)}
            </span>
          </button>
        ))}
      </div>

      {/* Current Quarter Content */}
      <div className="quarter-content">
        <div className="quarter-header">
          <h2>{roadmapData[activeTab].title}</h2>
          <p className="quarter-subtitle">{roadmapData[activeTab].subtitle}</p>
          <div className="status-badge" style={{ backgroundColor: getStatusColor(roadmapData[activeTab].status) }}>
            {getStatusText(roadmapData[activeTab].status)}
          </div>
        </div>

        <div className="features-grid">
          {roadmapData[activeTab].items.map((item, index) => (
            <div key={index} className="feature-card">
              <h3 className="feature-title">{item.title}</h3>
              <ul className="feature-list">
                {item.features.map((feature, idx) => (
                  <li key={idx} className="feature-item">
                    <span className="feature-icon">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* All Quarters Preview */}
      <div className="all-quarters-section">
        <h3>All Quarters at a Glance</h3>
        <div className="all-quarters-grid">
          {Object.keys(roadmapData).map((quarter) => (
            <div
              key={quarter}
              className={`quarter-preview ${activeTab === quarter ? 'active-preview' : ''}`}
              onClick={() => setActiveTab(quarter)}
            >
              <div className="preview-header">
                <span className="preview-quarter">{quarter.toUpperCase()}</span>
                <span
                  className="preview-status-dot"
                  style={{ backgroundColor: getStatusColor(roadmapData[quarter].status) }}
                />
              </div>
              <h4 className="preview-title">{roadmapData[quarter].title.split('–')[0]}</h4>
              <p className="preview-subtitle">
                {roadmapData[quarter].items.length} major initiatives
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback Section */}
      <div className={`feedback-section ${showFeedbackForm ? 'expanded' : ''}`}>
        <div className="feedback-header">
          <h3>Have Suggestions for Our Roadmap?</h3>
          <p>We value your input! Suggest features, report bugs, or share improvements to help shape Sageion's future.</p>

          {!showFeedbackForm && (
            <button
              className="show-feedback-button"
              onClick={() => setShowFeedbackForm(true)}
            >
              📝 Share Your Feedback
            </button>
          )}
        </div>

        {showFeedbackForm && (
          <div className="feedback-form-container">
            <form onSubmit={handleSubmitFeedback} className="feedback-form">
              <div className="form-grid">
                {/* Feedback Type */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="label-text">Type of Feedback</span>
                    <span className="required">*</span>
                  </label>
                  <div className="feedback-type-options">
                    {feedbackTypes.map(type => (
                      <label key={type.value} className="feedback-type-option">
                        <input
                          type="radio"
                          name="type"
                          value={type.value}
                          checked={feedbackData.type === type.value}
                          onChange={handleFeedbackChange}
                          className="type-radio"
                        />
                        <span className="type-emoji">{type.emoji}</span>
                        <span className="type-label">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="label-text">Title</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    placeholder="Brief description of your feedback"
                    value={feedbackData.title}
                    onChange={handleFeedbackChange}
                    className="form-input"
                    required
                  />
                </div>

                {/* Message */}
                <div className="form-group full-width">
                  <label className="form-label">
                    <span className="label-text">Details</span>
                    <span className="required">*</span>
                  </label>
                  <textarea
                    name="message"
                    placeholder="Describe your feedback in detail. What problem are you trying to solve? How would this help you?"
                    value={feedbackData.message}
                    onChange={handleFeedbackChange}
                    className="form-textarea"
                    rows="6"
                    required
                  />
                  <div className="character-count">
                    {feedbackData.message.length}/1000 characters
                  </div>
                </div>

                {/* Tags */}
                <div className="form-group full-width">
                  <label className="form-label">Tags (optional)</label>
                  <div className="tags-container">
                    <div className="predefined-tags">
                      {predefinedTags.map(tag => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleTagToggle(tag)}
                          className={`tag ${feedbackData.tags.includes(tag) ? 'selected' : ''}`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>

                    <div className="custom-tag-input">
                      <input
                        type="text"
                        placeholder="Add custom tag"
                        value={customTag}
                        onChange={(e) => setCustomTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomTag())}
                        className="tag-input"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomTag}
                        className="add-tag-button"
                      >
                        Add
                      </button>
                    </div>

                    {feedbackData.tags.length > 0 && (
                      <div className="selected-tags">
                        <span className="selected-tags-label">Selected:</span>
                        {feedbackData.tags.map(tag => (
                          <span key={tag} className="selected-tag">
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="remove-tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Info - Auto-filled from subscription if available */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="label-text">Your Email</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="user_email"
                    placeholder="you@example.com"
                    value={feedbackData.user_email || email}
                    onChange={handleFeedbackChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Your Name (optional)</label>
                  <input
                    type="text"
                    name="user_name"
                    placeholder="Your name"
                    value={feedbackData.user_name || name}
                    onChange={handleFeedbackChange}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowFeedbackForm(false);
                    setFeedbackData({
                      title: '',
                      message: '',
                      type: 'feature',
                      user_email: '',
                      user_name: '',
                      tags: []
                    });
                    setFeedbackStatus(null);
                  }}
                  className="cancel-button"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmittingFeedback}
                  className="submit-feedback-button"
                >
                  {isSubmittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>

              {feedbackStatus && (
                <div className={`feedback-status ${feedbackStatus.type}`}>
                  {feedbackStatus.message}
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicChatRoadmap;